import { GenerationApiError, AuthRequiredError } from "./generation-api.js";

const DEFAULT_BASE_URL = readBaseUrl();

function readBaseUrl() {
    const value = import.meta.env?.VITE_API_BASE_URL;
    if (typeof value !== "string") return "";
    return value.replace(/\/+$/, "");
}

function buildHeaders(accessToken) {
    const headers = { "content-type": "application/json" };
    if (accessToken) headers.authorization = `Bearer ${accessToken}`;
    return headers;
}

function asObject(value) {
    return value !== null && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function asStringArray(value) {
    return Array.isArray(value) ? value.map((item) => String(item ?? "")).filter(Boolean) : [];
}

function normalizeObservation(value) {
    const row = asObject(value);
    return {
        code: String(row.code || ""),
        severity: String(row.severity || ""),
        path: String(row.path || ""),
        message: String(row.message || ""),
        questionId: String(row.questionId || ""),
        suggestedAction: String(row.suggestedAction || "")
    };
}

function buildQuestionNormalizer(id) {
    if (id === "axis_name") {
        return (value) => {
            const next = String(value || "").trim();
            if (!next) throw new Error("軸名を入力してください。");
            return /軸$/.test(next) ? next : `${next}軸`;
        };
    }
    if (id === "timeout_sec") {
        return (value) => {
            const num = Number(value);
            if (!Number.isFinite(num) || num < 0) throw new Error("0 以上の数値を入力してください。");
            return String(num);
        };
    }
    if (id === "error_code") {
        return (value) => {
            const next = String(value || "").trim().toUpperCase();
            if (!/^E\d{2,4}$/.test(next)) throw new Error("E101 のような形式で入力してください。");
            return next;
        };
    }
    return null;
}

function normalizeQuestion(value) {
    const row = asObject(value);
    const id = String(row.id || "");
    return {
        id,
        kind: String(row.kind || "text"),
        text: String(row.text || ""),
        placeholder: String(row.placeholder || ""),
        quickOptions: asStringArray(row.quickOptions),
        options: Array.isArray(row.options)
            ? row.options.map((option) => ({
                value: String(asObject(option).value || ""),
                label: String(asObject(option).label || "")
            }))
            : [],
        normalize: buildQuestionNormalizer(id)
    };
}

function normalizeMemory(value) {
    const row = asObject(value);
    return {
        assumptions: asStringArray(row.assumptions),
        rejectedIdeas: asStringArray(row.rejectedIdeas),
        confirmedFacts: asStringArray(row.confirmedFacts),
        warnings: asStringArray(row.warnings)
    };
}

export function normalizeCreateAgentSessionPayload(value) {
    const row = asObject(value);
    return {
        mode: typeof row.mode === "string" ? row.mode : "flow",
        flowContext: asObject(row.flowContext),
        generationIr: asObject(row.generationIr),
        rawFlowIndex: asObject(row.rawFlowIndex),
        vendor: String(row.vendor || ""),
        projectId: typeof row.projectId === "string" ? row.projectId : undefined,
        userInstruction: String(row.userInstruction || ""),
        clientRequestId: typeof row.clientRequestId === "string" ? row.clientRequestId : undefined,
        answers: { ...asObject(row.answers) },
        memory: normalizeMemory(row.memory)
    };
}

export function normalizeAnswerAgentSessionPayload(value) {
    const row = asObject(value);
    return {
        userInstruction: String(row.userInstruction || ""),
        clientRequestId: typeof row.clientRequestId === "string" ? row.clientRequestId : undefined,
        expectedRevisionCount: Number.isFinite(Number(row.expectedRevisionCount))
            ? Number(row.expectedRevisionCount)
            : undefined,
        expectedUpdatedAt: typeof row.expectedUpdatedAt === "string" ? row.expectedUpdatedAt : undefined,
        answers: { ...asObject(row.answers) },
        memory: normalizeMemory(row.memory)
    };
}

export function normalizeAgentSessionResponse(value) {
    const row = asObject(value);
    const hasOwn = (key) => Object.prototype.hasOwnProperty.call(row, key);
    return {
        authenticated: Boolean(row.authenticated),
        sessionId: String(row.sessionId || ""),
        status: String(row.status || "idle"),
        jobStatus: String(row.jobStatus || "queued"),
        jobType: String(row.jobType || "flow_plan"),
        canPoll: row.canPoll !== false,
        isTerminalJobState: Boolean(row.isTerminalJobState),
        mode: String(row.mode || "flow"),
        traceId: typeof row.traceId === "string" ? row.traceId : "",
        jobId: typeof row.jobId === "string" ? row.jobId : "",
        workspaceId: typeof row.workspaceId === "string" ? row.workspaceId : "",
        projectId: typeof row.projectId === "string" ? row.projectId : "",
        generationRunId: typeof row.generationRunId === "string" ? row.generationRunId : "",
        clientRequestId: typeof row.clientRequestId === "string" ? row.clientRequestId : "",
        generationRunPersisted: Boolean(row.generationRunPersisted),
        vendor: String(row.vendor || ""),
        goal: String(row.goal || ""),
        ...(hasOwn("answers") ? { answers: { ...asObject(row.answers) } } : {}),
        ...(hasOwn("assumptions") ? { assumptions: asStringArray(row.assumptions) } : {}),
        observations: Array.isArray(row.observations) ? row.observations.map(normalizeObservation) : [],
        questions: Array.isArray(row.questions) ? row.questions.map(normalizeQuestion) : [],
        nextActions: asStringArray(row.nextActions),
        nextAction: String(row.nextAction || ""),
        ...(hasOwn("memory") ? { memory: normalizeMemory(row.memory) } : {}),
        currentStepIndex: Number.isFinite(Number(row.currentStepIndex)) ? Number(row.currentStepIndex) : 0,
        revisionCount: Number.isFinite(Number(row.revisionCount)) ? Number(row.revisionCount) : 0,
        ...(hasOwn("draft") ? { draft: row.draft ?? null } : {}),
        ...(hasOwn("revisedDraft") || hasOwn("draft") ? { revisedDraft: row.revisedDraft ?? row.draft ?? null } : {}),
        ...(hasOwn("generatedCode") ? { generatedCode: String(row.generatedCode || "") } : {}),
        startedAt: typeof row.startedAt === "string" ? row.startedAt : "",
        completedAt: typeof row.completedAt === "string" ? row.completedAt : "",
        cancelRequestedAt: typeof row.cancelRequestedAt === "string" ? row.cancelRequestedAt : "",
        queueWaitMs: Number.isFinite(Number(row.queueWaitMs)) ? Number(row.queueWaitMs) : null,
        executionDurationMs: Number.isFinite(Number(row.executionDurationMs)) ? Number(row.executionDurationMs) : null,
        errorCode: typeof row.errorCode === "string" ? row.errorCode : "",
        errorMessage: typeof row.errorMessage === "string" ? row.errorMessage : "",
        dispatchAttemptCount: Number.isFinite(Number(row.dispatchAttemptCount)) ? Number(row.dispatchAttemptCount) : 0,
        lastDispatchAttemptAt: typeof row.lastDispatchAttemptAt === "string" ? row.lastDispatchAttemptAt : "",
        lastDispatchError: typeof row.lastDispatchError === "string" ? row.lastDispatchError : "",
        conflictCount: Number.isFinite(Number(row.conflictCount)) ? Number(row.conflictCount) : 0,
        cancelCount: Number.isFinite(Number(row.cancelCount)) ? Number(row.cancelCount) : 0,
        fallbackCount: Number.isFinite(Number(row.fallbackCount)) ? Number(row.fallbackCount) : 0,
        usedFallback: Boolean(row.usedFallback),
        fallbackReason: row.fallbackReason ?? null,
        createdAt: String(row.createdAt || ""),
        updatedAt: String(row.updatedAt || ""),
        etag: typeof row.etag === "string" ? row.etag : "",
        responseView: typeof row.responseView === "string" ? row.responseView : "full",
        deltaFields: Array.isArray(row.deltaFields) ? row.deltaFields.map((value) => String(value || "")) : [],
        deltaBaseRevisionCount: Number.isFinite(Number(row.deltaBaseRevisionCount)) ? Number(row.deltaBaseRevisionCount) : null,
        revisionDeltaCount: Number.isFinite(Number(row.revisionDeltaCount)) ? Number(row.revisionDeltaCount) : null,
        hasRevisionDelta: typeof row.hasRevisionDelta === "boolean" ? row.hasRevisionDelta : null
    };
}

export function normalizeAgentSessionHistoryResponse(value) {
    const row = asObject(value);
    return {
        items: Array.isArray(row.items)
            ? row.items.map((item) => ({
                ...normalizeAgentSessionResponse(item),
                questionCount: Number.isFinite(Number(asObject(item).questionCount)) ? Number(asObject(item).questionCount) : 0
            }))
            : [],
        limit: Number.isFinite(Number(row.limit)) ? Number(row.limit) : 20
    };
}

async function readJson(response) {
    try {
        return await response.json();
    } catch {
        return null;
    }
}

function throwForResponse(response, parsed) {
    const message = typeof parsed?.message === "string" ? parsed.message : `HTTP ${response.status}`;
    if (response.status === 401) {
        throw new AuthRequiredError(message, { status: response.status, code: parsed?.error, details: parsed });
    }
    throw new GenerationApiError(message, { status: response.status, code: parsed?.error, details: parsed });
}

export async function createAgentSession(payload, { accessToken, signal, baseUrl } = {}) {
    const root = typeof baseUrl === "string" ? baseUrl.replace(/\/+$/, "") : DEFAULT_BASE_URL;
    const requestPayload = normalizeCreateAgentSessionPayload(payload);
    const response = await fetch(`${root}/api/agent-sessions`, {
        method: "POST",
        headers: buildHeaders(accessToken),
        body: JSON.stringify(requestPayload),
        signal
    });
    const parsed = await readJson(response);
    if (!response.ok) throwForResponse(response, parsed);
    return normalizeAgentSessionResponse(parsed);
}

export async function getAgentSession(sessionId, { accessToken, signal, baseUrl, ifNoneMatch, view, sinceRevision } = {}) {
    const root = typeof baseUrl === "string" ? baseUrl.replace(/\/+$/, "") : DEFAULT_BASE_URL;
    const headers = buildHeaders(accessToken);
    if (typeof ifNoneMatch === "string" && ifNoneMatch.trim()) {
        headers["if-none-match"] = ifNoneMatch.trim();
    }
    const query = new URLSearchParams();
    if (typeof view === "string" && view.trim()) {
        query.set("view", view.trim());
    }
    if (Number.isFinite(Number(sinceRevision))) {
        query.set("sinceRevision", String(Math.max(0, Math.floor(Number(sinceRevision)))));
    }
    const suffix = query.size ? `?${query.toString()}` : "";
    const response = await fetch(`${root}/api/agent-sessions/${encodeURIComponent(sessionId)}${suffix}`, {
        method: "GET",
        headers,
        signal
    });
    if (response.status === 304) {
        return {
            notModified: true,
            sessionId: String(sessionId || ""),
            etag: response.headers.get("etag") || ""
        };
    }
    const parsed = await readJson(response);
    if (!response.ok) throwForResponse(response, parsed);
    return {
        ...normalizeAgentSessionResponse(parsed),
        etag: response.headers.get("etag") || ""
    };
}

export async function answerAgentSession(sessionId, payload, { accessToken, signal, baseUrl } = {}) {
    const root = typeof baseUrl === "string" ? baseUrl.replace(/\/+$/, "") : DEFAULT_BASE_URL;
    const requestPayload = normalizeAnswerAgentSessionPayload(payload);
    const response = await fetch(`${root}/api/agent-sessions/${encodeURIComponent(sessionId)}/answers`, {
        method: "POST",
        headers: buildHeaders(accessToken),
        body: JSON.stringify(requestPayload),
        signal
    });
    const parsed = await readJson(response);
    if (!response.ok) throwForResponse(response, parsed);
    return normalizeAgentSessionResponse(parsed);
}

export async function cancelAgentSession(sessionId, { accessToken, signal, baseUrl } = {}) {
    const root = typeof baseUrl === "string" ? baseUrl.replace(/\/+$/, "") : DEFAULT_BASE_URL;
    const response = await fetch(`${root}/api/agent-sessions/${encodeURIComponent(sessionId)}/cancel`, {
        method: "POST",
        headers: buildHeaders(accessToken),
        signal
    });
    const parsed = await readJson(response);
    if (!response.ok) throwForResponse(response, parsed);
    return normalizeAgentSessionResponse(parsed);
}

export async function listAgentSessions({ accessToken, signal, baseUrl, limit, status } = {}) {
    const root = typeof baseUrl === "string" ? baseUrl.replace(/\/+$/, "") : DEFAULT_BASE_URL;
    const query = new URLSearchParams();
    if (Number.isFinite(Number(limit))) query.set("limit", String(Number(limit)));
    if (typeof status === "string" && status.trim()) query.set("status", status.trim());
    const suffix = query.size ? `?${query.toString()}` : "";
    const response = await fetch(`${root}/api/agent-sessions${suffix}`, {
        method: "GET",
        headers: buildHeaders(accessToken),
        signal
    });
    const parsed = await readJson(response);
    if (!response.ok) throwForResponse(response, parsed);
    return normalizeAgentSessionHistoryResponse(parsed);
}
